/**
 * Wires the abstract side-effect action names declared by
 * `exerciseMachine` to real subsystem calls via `IExerciseSceneFacade`.
 *
 * This is the only place where the machine's actions become real
 * imperative work. Tests can override the facade for unit tests; the
 * scene's facade implementation is the production path.
 */

import type { IExerciseSceneFacade } from './IExerciseSceneFacade';
import type { ExerciseContext, ExerciseEvent } from './exerciseMachine.types';
import { SIDE_EFFECT_ACTION_NAMES, type SideEffectActionName } from './exerciseMachine';

type ActionCallback = (params: { context: ExerciseContext; event: ExerciseEvent }) => void;

export function buildSceneActions(
  facade: IExerciseSceneFacade,
  dispatchEvent: (event: ExerciseEvent) => void,
): Record<SideEffectActionName, ActionCallback> {
  return {
    loadObjective: ({ context }) => {
      if (context.objectiveIndex !== undefined) {
        facade.loadObjective(context.objectiveIndex);
      }
    },

    unloadObjective: () => {
      facade.unloadObjective();
    },

    initExercise: ({ context }) => {
      if (context.objectiveIndex !== undefined) {
        facade.initExercise(context.objectiveIndex);
      }
    },

    beginPlayback: ({ event }) => {
      if (event.type === 'NOTE_PLAYED') {
        facade.beginPlayback(event.note, event.velocity);
      }
    },

    stopPlayback: () => {
      facade.stopPlayback();
    },

    evaluatePerformance: () => {
      facade.evaluatePerformance();
    },

    notifyShowExercise: () => {
      facade.notifyShowExercise();
    },

    notifyHideExercise: () => {
      facade.notifyHideExercise();
    },

    notifyExercisePassed: () => {
      facade.notifyExercisePassed();
    },

    notifyObjectiveComplete: ({ context }) => {
      if (context.objectiveIndex !== undefined) {
        const isLast = context.objectiveIndex >= context.totalObjectives - 1;
        facade.notifyObjectiveComplete(context.objectiveIndex, isLast);
      }
    },

    notifyObjectiveSkipped: ({ context }) => {
      if (context.objectiveIndex !== undefined) {
        const isLast = context.objectiveIndex >= context.totalObjectives - 1;
        facade.notifyObjectiveSkipped(context.objectiveIndex, isLast);
      }
    },

    requestSkipButton: () => {
      facade.requestSkipButton();
    },

    showPassAnimation: () => {
      facade.showPassAnimation(() => {
        dispatchEvent({ type: 'TRANSITION_COMPLETE' });
      });
    },

    showFailAnimation: ({ context }) => {
      facade.showFailAnimation(context.failedAttempts);
    },

    pauseScene: () => {
      facade.pauseScene();
    },

    resumeScene: () => {
      facade.resumeScene();
    },

    cleanup: () => {
      facade.cleanup();
    },

    unloadGraphics: () => {
      facade.unloadGraphics();
    },

    playNarration: ({ context }) => {
      if (context.objectiveIndex !== undefined) {
        facade.playNarration(context.objectiveIndex);
      }
    },

    scheduleStop: () => {
      facade.scheduleStop();
    },

    showNextRing: () => {
      facade.showNextRing();
    },
  };
}

type _CoversAll = Record<(typeof SIDE_EFFECT_ACTION_NAMES)[number], ActionCallback>;
const _typeCheck: _CoversAll = buildSceneActions({} as IExerciseSceneFacade, () => {});
void _typeCheck;
